<template>
  <div
    ref="section"
    class="basic-hero">
    <div
      class="basic-hero-content inner-container"
      :class="{ 'no-img': !image }">
      <div class="basic-hero-content-text">
        <h1
          class="basic-hero-content-text-title"
          v-html="title" />
        <span
          v-if="subtitle"
          class="basic-hero-content-text-sub"
          v-html="subtitle" />
        <Button
          v-if="button"
          class="back-button"
          :text="button.text"
          :url="button.url"
          :target="button.target" />
      </div>
      <img
        v-if="image"
        class="basic-hero-content-img"
        :src="image" />
    </div>
    <div class="vfx-container">
      <div class="vfx inner-container">
        <div class="line-w-circle">
          <div
            ref="line"
            class="line"
            :class="`line-l line-gradient`"></div>
          <div
            class="sphere-rails"
            :class="`line-l`">
            <div
              ref="sphere"
              class="sphere" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import Button from '@/components/utility/ButtonGlobal.vue'

import useUtils from '@/composables/useUtils'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

defineProps(['title', 'subtitle', 'image', 'button'])

const { genUUID } = useUtils()
const animID = ref(genUUID())
const sphere = ref()
const line = ref()
const section = ref()

const sphereAnimIn = () => {
  gsap.fromTo(
    sphere.value,
    {
      y: -100,
    },
    {
      y: 0,
      ease: 'power1.out',
      scrollTrigger: {
        trigger: section.value,
        scrub: true,
        start: 'top top',
        end: 'bottom top+=10%',
        // markers: true,
        id: animID.value,
      },
    }
  )
}
onMounted(() => {
  sphereAnimIn()
})
onBeforeUnmount(() => {
  const anim = ScrollTrigger.getById(animID.value)
  if (anim) anim.kill()
})
</script>

<style lang="scss">
.basic-hero {
  @apply flex relative bg-lm_purple pt-20 min-h-[506px] lg:pb-11;

  &-content {
    @apply pt-[62px] flex flex-col items-center gap-5 pl-[55px] md:pl-[100px] lg:flex-row lg:items-end lg:justify-between lg:pt-0 lg:px-[180px];

    &.no-img {
      @apply justify-end pb-10 lg:justify-start lg:pb-0;
    }

    &-text {
      @apply flex flex-col gap-[5px] lg:w-[60%] lg:gap-6;

      &-title {
        @apply text-white font-nuances text-[44px] leading-[48px] tracking-[-0.44px] xl:heading-2;
      }

      &-sub {
        @apply small text-white;
      }
    }

    &-img {
      @apply object-cover w-[341px] h-[341px] -mb-[171px] lg:w-[377px] lg:h-[377px] lg:mb-0 lg:translate-y-[81px];
    }
  }

  .vfx-container {
    @apply absolute top-0 w-full h-full pointer-events-none pt-[80px] lg:pt-[110px];

    .vfx {
      @apply h-full w-full;

      .line-w-circle {
        @apply relative h-full w-full;

        .line,
        .sphere-rails {
          @apply absolute top-0 w-[1px] h-full flex items-center justify-center overflow-visible bg-white;

          &-l {
            @apply left-0;
          }

          .sphere {
            @apply min-w-[30px] min-h-[30px] rounded-full bg-white blur-[2px];
            box-shadow: 0 0 20px #ffffff;
          }
        }
      }
    }
  }
}
</style>
